/* Domovoi · a product's sections inside the app shell (D2.1).

   On the web each product has its own two-bar header (the dark site bar and
   the product nav). In the app the shell draws one header for everything and
   each product puts its sections here instead, as a row of chips that sticks
   under the shell header. `tools` (Curated's search and region, Considered's
   search) go up into the shell header, next to the bell, while that tab is
   showing: the chip row has no room for them on a phone.

   A pure component, no side effects, so products may import it. Styles live
   in src/shell/shell.css, which only shell.html loads. */
import React from 'react';
import { createPortal } from 'react-dom';
import { isWide, onWide } from '../core/layout.js';

export function ShellChips({ product, items, active, onSelect, tools, label, seg }) {
  const row = React.useRef(null);
  // Keep the active chip in view when it changes (Considered's later chips
  // start off-screen on a phone).
  React.useEffect(() => {
    const el = row.current && row.current.querySelector('.dv-chip.on');
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [active]);
  const slot = toolsSlot(product);
  // The redesign's segmented control (a few sections, all visible): the design system's .dv-seg.
  if (seg) return (
    <div className="dv-chips dv-segbar">
      <div className="dv-seg" role="radiogroup" aria-label={label}>
        {items.map((it) => (
          <button key={it.id} type="button" role="radio" data-id={it.id} aria-checked={it.id === active ? 'true' : 'false'} onClick={() => onSelect(it.id)}>{it.label}</button>
        ))}
      </div>
      {tools && slot ? createPortal(tools, slot) : null}
    </div>
  );
  return (
    <div className="dv-chips" role="tablist" aria-label={label}>
      <div className="dv-chips-row" ref={row}>
        {items.map((it) => (
          <button key={it.id} type="button" role="tab" data-id={it.id} aria-selected={it.id === active}
            className={'dv-chip' + (it.id === active ? ' on' : '')} onClick={() => onSelect(it.id)}>
            {it.label}{it.count ? <span className="dv-chip-n">{it.count}</span> : null}
          </button>
        ))}
      </div>
      {tools && slot ? createPortal(tools, slot) : null}
    </div>
  );
}

/* A product's head on the web, in the redesign (the canvas's Web boards; styles in src/ui/web-frame.css): its name,
   its sections as the design system's segmented control, and its tools on the right (search, Narrow the deck…).
   The web's nav is above it, in the page. */
export function WebHead({ name, items, active, onSelect, tools, chips }) {
  return (
    <div className={'pw-head' + (chips ? ' pw-head-chips' : '')}>
      <h1>{name}</h1>
      {/* more sections than a segmented control holds (Considered's five): chips */}
      {items && items.length && chips ? (
        <div className="pw-chips" role="radiogroup" aria-label={name}>
          {items.map((it) => (
            <button key={it.id} type="button" className="dv-chip" role="radio" aria-checked={it.id === active ? 'true' : 'false'} onClick={() => onSelect(it.id)}>{it.label}</button>
          ))}
        </div>
      ) : items && items.length ? (
        <div className="dv-seg" role="radiogroup" aria-label={name}>
          {items.map((it) => (
            <button key={it.id} type="button" role="radio" aria-checked={it.id === active ? 'true' : 'false'} onClick={() => onSelect(it.id)}>{it.label}</button>
          ))}
        </div>
      ) : null}
      <div className="pw-tools"><span className="pw-slot" id="pw-slot" />{tools}</div>
    </div>
  );
}

/** On a computer, on the web (the canvas's Web boards): the layouts that need more than CSS. */
export function useWide() {
  const [w, setW] = React.useState(isWide);
  React.useEffect(() => onWide(setW), []);
  return w;
}

/** What a screen puts up in its product's head on a computer (WebHead's #pw-slot, or another slot by id) — the
    paste-a-link field on Catered's Recipes, say. Renders nothing until the slot is in the page. */
export function WebSlot({ id, children }) {
  const [el, setEl] = React.useState(null);
  React.useEffect(() => { setEl(document.getElementById(id || 'pw-slot')); }, [id]);
  return el ? createPortal(children, el) : null;
}

/* This product's place in the shell header (shell.html's #dv-shell-tools);
   the shell shows only the current tab's. */
function toolsSlot(product) {
  const host = typeof document !== 'undefined' && document.getElementById('dv-shell-tools');
  if (!host || !product) return null;
  let el = host.querySelector('[data-tools="' + product + '"]');
  if (!el) { el = document.createElement('div'); el.className = 'dv-shell-tools-set'; el.dataset.tools = product; host.appendChild(el); }
  return el;
}

/* Tapping the tab you're already on scrolls to the top, and a second tap
   (already at the top) sends the product back to its first section. Products
   listen here; `fn` gets nothing. */
export function useShellRetap(product, fn) {
  const ref = React.useRef(fn);
  ref.current = fn;
  React.useEffect(() => {
    const on = (e) => { if (e.detail && e.detail.product === product) ref.current(); };
    window.addEventListener('dv-shell-home', on);
    return () => window.removeEventListener('dv-shell-home', on);
  }, [product]);
}
